import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  BadgeCheck,
  Eye,
  Sparkles,
  Truck,
} from 'lucide-react';
import { Product } from '../types';
import { ImageWithFallback } from './ImageWithFallback';

interface HomeFeaturedProductsProps {
  products: Product[];
}

type ProductWithDatabaseFields = Product & {
  stock_level?: number;
};

function getStockLevel(product: Product): number {
  const databaseProduct = product as ProductWithDatabaseFields;
  return Number(databaseProduct.stock_level ?? product.stockLevel ?? 0);
}

function getProductImage(product: Product): string {
  return product.images?.[0]?.url || '';
}

function formatPrice(price: number): string {
  return price.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
}

function conditionLabel(product: Product): string {
  return product.category || 'Used';
}

export function HomeFeaturedProducts({ products }: HomeFeaturedProductsProps) {
  const featuredProduct = products[0];
  const supportingProducts = products.slice(1, 5);

  if (!featuredProduct) {
    return (
      <div className="rounded-3xl border border-slate-200 bg-white py-12 text-center shadow-sm">
        <p className="text-slate-500">No products available at the moment.</p>
      </div>
    );
  }

  const featuredStock = getStockLevel(featuredProduct);

  return (
    <div>
      <div className="mb-8 flex flex-col gap-5 sm:mb-10 md:flex-row md:items-end md:justify-between">
        <div>
          <div className="mb-3 flex items-center gap-3 text-sm font-extrabold uppercase tracking-[0.18em] text-blue-700">
            <span className="h-0.5 w-8 bg-yellow-400" />
            Featured appliances
          </div>
          <h2 className="text-3xl font-black tracking-tight text-slate-950 sm:text-4xl lg:text-5xl">
            Handpicked. Quality. Value.
          </h2>
        </div>

        <Link
          to="/shop"
          className="inline-flex w-fit items-center gap-2 text-sm font-bold text-blue-700 transition hover:text-blue-900"
        >
          View all appliances
          <ArrowRight className="h-4 w-4" aria-hidden="true" />
        </Link>
      </div>

      <div className="grid gap-6 lg:grid-cols-[1.35fr_1fr]">
        <Link
          to={`/product/${featuredProduct.id}`}
          className="group overflow-hidden rounded-3xl border border-blue-900/10 bg-gradient-to-br from-blue-950 via-blue-900 to-blue-700 shadow-[0_24px_60px_rgba(15,23,42,0.18)] transition duration-300 hover:-translate-y-1 hover:shadow-[0_30px_70px_rgba(15,23,42,0.24)]"
        >
          <div className="grid min-h-[520px] md:grid-cols-[0.88fr_1.12fr]">
            <div className="flex flex-col justify-between p-7 text-white sm:p-9 lg:p-10">
              <div>
                <div className="mb-6 inline-flex items-center gap-2 rounded-full bg-yellow-400 px-4 py-2 text-xs font-black uppercase tracking-[0.14em] text-slate-950">
                  <Sparkles className="h-4 w-4" aria-hidden="true" />
                  Top pick
                </div>

                <div className="mb-4 flex flex-wrap items-center gap-2">
                  <span className="rounded-full bg-white/12 px-3 py-1.5 text-xs font-bold text-white ring-1 ring-inset ring-white/20">
                    {conditionLabel(featuredProduct)}
                  </span>
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-400/15 px-3 py-1.5 text-xs font-bold text-emerald-100 ring-1 ring-inset ring-emerald-300/30">
                    <span className="h-2 w-2 rounded-full bg-emerald-300" />
                    {featuredStock > 0 ? 'In stock' : 'Out of stock'}
                  </span>
                </div>

                <p className="mb-2 text-sm font-extrabold uppercase tracking-[0.18em] text-blue-200">
                  {featuredProduct.brand}
                </p>
                <h3 className="text-3xl font-black leading-tight text-white sm:text-4xl">
                  {featuredProduct.name}
                </h3>

                <div className="mt-6 space-y-3 text-sm text-blue-100">
                  <div className="flex items-center gap-3">
                    <BadgeCheck className="h-5 w-5 text-yellow-300" aria-hidden="true" />
                    Inspected before delivery
                  </div>
                  <div className="flex items-center gap-3">
                    <Truck className="h-5 w-5 text-yellow-300" aria-hidden="true" />
                    Delivery and installation available
                  </div>
                </div>
              </div>

              <div className="mt-10">
                <p className="mb-4 text-4xl font-black text-yellow-300 sm:text-5xl">
                  {formatPrice(featuredProduct.price)}
                </p>
                <span className="inline-flex items-center gap-2 rounded-xl bg-yellow-400 px-5 py-3.5 text-sm font-black text-slate-950 transition group-hover:bg-yellow-300">
                  <Eye className="h-5 w-5" aria-hidden="true" />
                  View appliance
                  <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </span>
              </div>
            </div>

            <div className="relative min-h-[360px] overflow-hidden bg-gradient-to-br from-slate-50 to-slate-200 md:min-h-full">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_45%,rgba(255,255,255,0.98),rgba(226,232,240,0.72)_68%,rgba(203,213,225,0.9))]" />
              <ImageWithFallback
                src={getProductImage(featuredProduct)}
                alt={featuredProduct.name}
                fallbackText={featuredProduct.name}
                className="relative z-10 h-full w-full object-contain p-6 transition duration-500 group-hover:scale-[1.035] sm:p-10"
              />
            </div>
          </div>
        </Link>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {supportingProducts.map((product) => {
            const stockLevel = getStockLevel(product);

            return (
              <Link
                key={product.id}
                to={`/product/${product.id}`}
                className="group flex min-h-[250px] flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition duration-300 hover:-translate-y-1 hover:border-blue-200 hover:shadow-xl"
              >
                <div className="relative flex h-40 items-center justify-center overflow-hidden bg-gradient-to-br from-slate-50 to-slate-100">
                  <span className="absolute left-3 top-3 z-20 rounded-full bg-blue-700 px-2.5 py-1 text-[10px] font-black uppercase tracking-wide text-white">
                    {conditionLabel(product)}
                  </span>
                  <span className="absolute right-3 top-3 z-20 inline-flex items-center gap-1.5 rounded-full bg-white/95 px-2.5 py-1 text-[10px] font-bold text-slate-700 shadow-sm">
                    <span
                      className={`h-1.5 w-1.5 rounded-full ${
                        stockLevel > 0 ? 'bg-emerald-500' : 'bg-red-500'
                      }`}
                    />
                    {stockLevel > 0 ? 'In stock' : 'Sold out'}
                  </span>
                  <ImageWithFallback
                    src={getProductImage(product)}
                    alt={product.name}
                    fallbackText={product.name}
                    className="h-full w-full object-contain p-4 transition duration-500 group-hover:scale-105"
                  />
                </div>

                <div className="flex flex-1 flex-col p-4">
                  <p className="mb-1 text-[11px] font-extrabold uppercase tracking-[0.14em] text-blue-700">
                    {product.brand}
                  </p>
                  <h3 className="line-clamp-2 text-sm font-bold leading-5 text-slate-900 sm:text-base">
                    {product.name}
                  </h3>

                  <div className="mt-auto flex items-end justify-between gap-3 pt-4">
                    <p className="text-2xl font-black text-slate-950">
                      {formatPrice(product.price)}
                    </p>
                    <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full border border-blue-100 bg-blue-50 text-blue-700 transition group-hover:border-blue-700 group-hover:bg-blue-700 group-hover:text-white">
                      <ArrowRight className="h-4 w-4" aria-hidden="true" />
                    </span>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>

      <div className="mt-8 flex justify-center">
        <Link
          to="/shop"
          className="inline-flex items-center gap-2 rounded-full bg-blue-700 px-7 py-3.5 text-sm font-black text-white shadow-md transition hover:-translate-y-0.5 hover:bg-blue-600 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
        >
          View Full Inventory
          <ArrowRight className="h-4 w-4" aria-hidden="true" />
        </Link>
      </div>
    </div>
  );
}
